<template>
  <b-overlay :show="showOverview" no-center rounded="sm" opacity="1" variant="light">
    <AppSection
      id="question-form-page"
      :title="$getConst.pages.help_configuration.help_configuration_page_title"
      type="section"
      select-language
    >
      <b-form-group id="help-page-language" :label="$t($getConst.pages.help_configuration.help_configuration_language_label)" label-for="help-page-language-input">
        <multiselect
          class="border-right-0 border-left-0"
          v-model="language"
          track-by="id"
          label="name"
          :placeholder="$t(this.$getConst.pages.form.select_option)"
          :options="languages"
          :disabled="isSubmitting || isLoading"
          :allowEmpty="false"
          @input="getHelpContent"
        >
          <template slot="singleLabel" slot-scope="props">
            <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
            {{$t(props.option.name) | capitalize}}
          </template>
          <template slot="option" slot-scope="props">
            <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
            {{$t(props.option.name.toLowerCase()) | capitalize}}
          </template>
          <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
        </multiselect>
      </b-form-group>

      <vue-editor
        v-if="!isLoading"
        :disabled="isSubmitting"
        :customModules="customModulesForEditor"
        :editorOptions="editorSettings"
        :editorToolbar="toolbarOptions"
        v-model.trim="$v.helpContentToEdit.$model"
        :class="{ 'is-invalid': $v.helpContentToEdit.$error }"
        class="mb-4"
      />
      <AppFormFeedback :errors="$v.helpContentToEdit"/>

        <div class="row">
          <div class="col-lg-6 col-md-12">
            <Attachments
              v-if="!isLoading"
              :scope-id="this.scope_id"
              :entity-type="this.helpEntity"
              :entity-id="this.helpId"
              :file-infos="attachments"
            />
          </div>
        </div>
      <a
        type="button"
        class="d-block text-center text-uppercase font-weight-bold text-secondary pt-3"
        v-if="!isLoading"
        @click="showOverview = !showOverview"
      >
        {{$t($getConst.pages.help_configuration.help_configuration_overview) | capitalize}}
      </a>

      <AppConfirmButtons
        v-if="!isLoading"
        :first-button="hasPermission(this.$getConst.permissions.writeHelpContents)"
        :cancel-text="$getConst.pages.button.cancel_changes"
        :is-loading="isSubmitting"
        @onSubmit="onSave"
        @onCancel="onReset"
      />

      <AppSpinner
        v-if="isLoading"
        size="5"
        class="mx-auto mt-5 text-secondary"
      />
    </AppSection>
    <template #overlay>
      <AppSection
        id="help-page-overview"
        type="section"
        title="overview"
        class="p-2"
        @onClick="showOverview = !showOverview"
        :button="true"
        :button-icon="$getConst.icons.close"
        button-variant="danger"
        button-size="sm"
      >
        <div v-html="helpContentToEdit"/>
      </AppSection>
    </template>
  </b-overlay>
</template>

<script>
  import AppSection from "../../../../components/common/sections/AppSection";
  import {VueEditor} from "vue2-editor";
  import ImageResize from "quill-image-resize-vue";
  import { required } from "vuelidate/lib/validators";
  import AppConfirmButtons from "../../../../components/common/utils/AppConfirmButtons";
  import AppFormFeedback from "../../../../components/common/form/AppFormFeedback";
  import {
    getScopeLanguageHelpContent,
    getScopeLanguagesWithDefaultHelp,
    updateScopeHelpContent
  } from "../../../../api/helpContent.api";
  import AppSpinner from "../../../../components/common/utils/AppSpinner";
  import Attachments from "../../../../components/common/attachments/Attachments";

  export default {
    name: "index",
    components: {Attachments, AppSpinner, AppFormFeedback, AppConfirmButtons, AppSection, VueEditor},
    props: {
      scope_id: null
    },
    data() {
      return {
        customModulesForEditor: [{ alias: "imageResize", module: ImageResize }],
        toolbarOptions: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'script': 'sub'}, { 'script': 'super' }],
          [{ 'color': ['#3c4b64','#002776','#00a0da','#e55353','#80379b','#28a745','#e98300'] }],
          ['link', 'image', 'video']
        ],
        editorSettings: {
          modules: {
            imageResize: {}
          }
        },

        showOverview: false,
        helpContent: null,
        helpEntity: "help",
        helpId: null,
        helpContentToEdit: null,
        language: null,
        languages: [],
        attachments: [],
        isLoading: false,
        isSubmitting: false
      }
    },
    validations: {
      helpContentToEdit: {required}
    },
    methods: {
      getScopeLanguages() {
        this.isLoading = true;
        getScopeLanguagesWithDefaultHelp(this.scope_id).then(
          (response) => {
            this.languages = response.data;
            this.language = this.languages.find(lang => lang.hasOwnProperty("help_content"));
            if (!this.language) {
              this.language = this.languages[0];
            }
            this.helpId = this.language.help_id;
            this.helpContent = this.language.help_content;
            this.attachments = this.language.attachments;
            this.helpContentToEdit = this.language.help_content;
            this.isLoading = false;
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
      },
      getHelpContent(language) {
        this.isLoading = true;
        getScopeLanguageHelpContent(this.scope_id, language.id).then(
          (response) => {
            this.language = response.data.language;
            this.helpId = response.data.help_id;
            this.helpContent = response.data.help_content;
            this.attachments = response.data.attachments;
            this.helpContentToEdit = response.data.help_content;
            this.isLoading = false;
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
      },
      onSave() {
        this.isSubmitting = true;
        updateScopeHelpContent(this.scope_id, this.helpId, this.helpContentToEdit).then(
          (response) => {
            this.helpContent = response.data.help_content;
            this.isSubmitting = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
          },
          (error) => {
            this.handleApiError(error);
            this.isSubmitting = false;
          }
        );
      },
      onReset() {
        this.helpContentToEdit = this.helpContent;
      }
    },
    created() {
      this.getScopeLanguages();
    }
  }
</script>

<style scoped>
  .not-collapsed .collapse-toggle{
    transform: rotate(180deg);
    transition: .5s;
  }
  .collapsed .collapse-toggle{
    transform: rotate(0deg);
    transition: .5s;
  }
</style>
